import { useState } from "react";
import { Radio, Unlink, KeyRound, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { api, errMsg } from "@/lib/api";

/**
 * Manual admin controls for the KYRALINK firmware binding (ingest.py /
 * server.py's /devices/{id}/firmware/* routes) — normally the mobile app
 * auto-binds firmware_device_id on first BLE connect, but pairing a demo
 * unit (a different id, "KD-...", from the same physical board's real
 * "KL-..." id) or recovering from a mistaken bind needs a manual path.
 * Before this, the only way to reach these routes was a raw curl call.
 */
export default function FirmwarePairingCard({ device, onChange }) {
  const [manualId, setManualId] = useState("");
  const [busy, setBusy] = useState(false);
  const [unbindOpen, setUnbindOpen] = useState(false);
  const [newKey, setNewKey] = useState(null); // { firmware_device_id, ingest_key } | null
  const [copied, setCopied] = useState(false);

  const bound = Boolean(device.firmware_bound);

  const bind = async () => {
    const id = manualId.trim();
    if (!id) return;
    setBusy(true);
    try {
      await api.post(`/devices/${device.id}/firmware/bind`, { firmware_device_id: id });
      toast.success(`Bound to ${id}`);
      setManualId("");
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const unbind = async () => {
    setUnbindOpen(false);
    setBusy(true);
    try {
      await api.post(`/devices/${device.id}/firmware/unbind`);
      toast.success("Firmware id unbound — the ingest key was cleared too");
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const generateKey = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/devices/${device.id}/firmware/ingest-key`);
      setNewKey(data);
      setCopied(false);
      // Refreshes the parent's device data so has_ingest_key flips true and
      // the button disables right away — without this it only updates on
      // the next full page reload, since this component only holds the key
      // dialog's own local state, not the device record itself.
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(newKey.ingest_key);
      setCopied(true);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Could not copy — select and copy the key manually");
    }
  };

  return (
    <div className="border border-kl-lineLight p-6" data-testid="firmware-pairing-card">
      <div className="flex items-center gap-2 mb-4">
        <Radio size={14} className="text-kl-mutedLight" />
        <p className="kl-label">Firmware pairing</p>
      </div>

      {bound ? (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs text-kl-mutedLight">Bound hardware id</p>
            <p className="font-mono text-sm mt-0.5" data-testid="firmware-device-id">{device.firmware_device_id}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={generateKey}
              disabled={busy || device.has_ingest_key}
              className="kl-btn-outline"
              data-testid="firmware-generate-key-button"
              title={device.has_ingest_key ? "An ingest key was already generated for this device — unbind to issue a new one" : undefined}
            >
              <KeyRound size={14} /> {busy ? "Working…" : device.has_ingest_key ? "Ingest key generated" : "Generate ingest key"}
            </button>
            <button onClick={() => setUnbindOpen(true)} disabled={busy} className="kl-btn-outline hover:bg-kl-inactive hover:border-kl-inactive" data-testid="firmware-unbind-button">
              <Unlink size={14} /> Unbind
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs text-kl-mutedLight" htmlFor="firmware-bind-input">Hardware device id (e.g. KL-91ARC76W72AC)</label>
            <input
              id="firmware-bind-input"
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && bind()}
              placeholder="KL-98EBC66E27AC"
              className="kl-input-box h-9 w-full font-mono text-sm mt-1"
              data-testid="firmware-bind-input"
            />
          </div>
          <button onClick={bind} disabled={busy || !manualId.trim()} className="kl-btn-primary h-9" data-testid="firmware-bind-button">
            {busy ? "Binding…" : "Bind"}
          </button>
        </div>
      )}

      <ConfirmDialog
        open={unbindOpen}
        onOpenChange={setUnbindOpen}
        title="Unbind this firmware id?"
        description={`${device.firmware_device_id} will be freed for rebinding elsewhere, and this device's ingest key is cleared. Archived telemetry under that id is not deleted.`}
        confirmLabel="Unbind"
        onConfirm={unbind}
      />

      <Dialog open={Boolean(newKey)} onOpenChange={(open) => !open && setNewKey(null)}>
        <DialogContent className="max-w-lg rounded-none border-kl-black p-0 gap-0" data-testid="ingest-key-dialog">
          <DialogHeader className="bg-kl-black text-white p-6">
            <DialogTitle className="font-display font-light text-2xl text-white">New ingest key</DialogTitle>
            <DialogDescription className="text-kl-muted">
              For {newKey?.firmware_device_id} — shown once. Paste this into the unit's <span className="font-mono">DeviceConfig.h</span> (<span className="font-mono">DeviceApiKey</span>) before flashing.
            </DialogDescription>
          </DialogHeader>
          <div className="p-6 flex flex-col gap-3">
            <div className="border border-kl-lineLight p-3 font-mono text-xs break-all bg-kl-offWhite" data-testid="ingest-key-value">
              {newKey?.ingest_key}
            </div>
            <button onClick={copyKey} className="kl-btn-outline w-full h-10" data-testid="ingest-key-copy-button">
              {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy key"}
            </button>
            <button onClick={() => setNewKey(null)} className="kl-btn-ghost w-full h-9" data-testid="ingest-key-done-button">Done</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
